// ✅ 모바일 전용 SCAREditor - 웹사이트 코드에 영향 없음
import React, { useEffect, useMemo, useState, useRef } from "react";
import TinyMCEeditor from "../../const/TinyMCEeditor";
import MobileReferenceModal from "../../approval/MobileReferenceModal";
import MobileAttachmentBox from "../../common/papers/MobileAttachmentBox";

export default function MobileSCAREditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const editorRef = useRef(null);
  const attachmentRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const meId = me?.id || null;

  /* ================================
     기본 상태
  ================================ */
  const STATUS_BY_TYPE = [
    { value: "action_needed", label: "조치 필요" },
    { value: "in_progress", label: "조치 중" },
    { value: "urgent", label: "긴급" },
    { value: "cancelled", label: "취소" },
  ];

  const [title, setTitle] = useState(initial.title || "");
  const [progress, setProgress] = useState(initial.progress || "action_needed");
  const [author, setAuthor] = useState(initial.author || me?.name || "");
  const [docDate, setDocDate] = useState(initial.doc_date || today);
  const [instructions, setInstructions] = useState(
    initial.meta?.instructions || initial.instructions || ""
  );

  /* 상세 내용 */
  const [scarProblem, setScarProblem] = useState(
    initial.scarProblem || initial.meta?.scarProblem || ""
  );
  const [scarImmediate, setScarImmediate] = useState(
    initial.scarImmediate || initial.meta?.scarImmediate || ""
  );
  const [scarRootCause, setScarRootCause] = useState(
    initial.scarRootCause || initial.meta?.scarRootCause || ""
  );
  const [showDetailTable, setShowDetailTable] = useState(false);

  /* ================================
      담당자(참조자)
  ================================ */
  const [referenceUserIds, setReferenceUserIds] = useState([]);
  const [referenceUserDetails, setReferenceUserDetails] = useState([]);
  const [isReferenceModalOpen, setIsReferenceModalOpen] = useState(false);

  const openReferenceModal = () => setIsReferenceModalOpen(true);
  const closeReferenceModal = () => setIsReferenceModalOpen(false);

  const handleRefChange = (ids) => setReferenceUserIds(ids);
  const handleRefSave = (ids, details) => {
    setReferenceUserIds(ids);
    setReferenceUserDetails(details || []);
    return true;
  };

  /* ================================
      🔥 알림 / 긴급 알림
  ================================ */
  const [sendAlert, setSendAlert] = useState(
    initial.sendAlert ?? initial.meta?.send_alert ?? false
  );
  const [alertUrgent, setAlertUrgent] = useState(
    initial.alertUrgent ?? initial.meta?.alert_urgent ?? false
  );

  /* ================================
      자동 제목
  ================================ */
  const autoTitle = useMemo(() => {
    const label =
      STATUS_BY_TYPE.find((s) => s.value === progress)?.label || "조치 필요";
    return `[SCAR] ${label} - ${today}`;
  }, [progress, today]);

  const toggleDetailTable = () => setShowDetailTable(!showDetailTable);

  /* ================================
      부모로 meta 전달
  ================================ */
  useEffect(() => {
    const html =
      editorRef.current?.getContent?.({ format: "html" }) || instructions;

    onChange?.({
      dns: initial.dns || "",
      title: (title ?? "").trim() || autoTitle,
      author,
      doc_type: "scar",
      status: "none",
      attachmentRef,
      meta: {
        doc_date: docDate,
        progress,
        instructions: html,
        scarProblem,
        scarImmediate,
        scarRootCause,
        reference_users: referenceUserIds,
        send_alert: sendAlert,
        alert_urgent: alertUrgent,
      },
    });
  }, [
    title,
    autoTitle,
    author,
    docDate,
    progress,
    instructions,
    scarProblem,
    scarImmediate,
    scarRootCause,
    referenceUserIds,
    sendAlert,
    alertUrgent,
    onChange,
    initial.dns,
  ]);

  const sectionStyle = "rounded-lg border bg-white p-3 min-w-0 w-full mb-4";

  return (
    <div
      className="w-full min-w-0 overflow-x-hidden flex flex-col items-center px-1 space-y-4"
      style={{ maxWidth: "calc(100vw - 0.5rem)" }}
    >
      {/* 문서 기본 정보 */}
      <section className={sectionStyle}>
        <div className="text-sm font-semibold mb-4 text-gray-900">
          문서 정보
        </div>

        <div className="space-y-4 w-full">
          {/* 제목 */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-600 font-medium">제목</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={title}
              placeholder={autoTitle}
              onChange={(e) => setTitle(e.target.value)}
              disabled={readOnly}
            />
          </div>

          {/* 문서 유형 + 상태 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                문서유형
              </label>
              <div className="text-sm text-gray-900 py-2">SCAR</div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">상태</label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2">
                  {STATUS_BY_TYPE.find((s) => s.value === progress)?.label ||
                    "-"}
                </div>
              ) : (
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={progress}
                  onChange={(e) => setProgress(e.target.value)}
                >
                  {STATUS_BY_TYPE.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* 작성자 + 날짜 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                작성자
              </label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2">{author}</div>
              ) : (
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                />
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                작성일자
              </label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2">{docDate}</div>
              ) : (
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={docDate}
                  onChange={(e) => setDocDate(e.target.value)}
                />
              )}
            </div>
          </div>

          {/* SCAR 작성 */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-gray-600 font-medium">
              SCAR 작성
            </label>
            {readOnly ? (
              <div className="text-sm text-gray-900 py-2 whitespace-pre-wrap break-words">
                {instructions || "-"}
              </div>
            ) : (
              <TinyMCEeditor
                initialValue={instructions}
                editorRef={editorRef}
                onChange={(v) => setInstructions(v)}
              />
            )}
          </div>
        </div>

        {/* 상세 내용 표 토글 버튼 */}
        <div className="mt-4">
          <button
            className="btn-basic text-sm px-3 py-1.5"
            onClick={toggleDetailTable}
            type="button"
          >
            {showDetailTable ? "상세 표 닫기" : "상세 내용 표로 입력"}
          </button>
        </div>

        {/* 상세 내용 표 */}
        {showDetailTable && (
          <div className="mt-4 space-y-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                안전 위반 사항
              </label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2 whitespace-pre-wrap break-words">
                  {scarProblem || "-"}
                </div>
              ) : (
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={scarProblem}
                  onChange={(e) => setScarProblem(e.target.value)}
                  placeholder="안전 위반 사항을 입력하세요"
                />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                즉시 조치 사항
              </label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2 whitespace-pre-wrap break-words">
                  {scarImmediate || "-"}
                </div>
              ) : (
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={scarImmediate}
                  onChange={(e) => setScarImmediate(e.target.value)}
                  placeholder="즉시 조치 사항을 입력하세요"
                />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-gray-600 font-medium">
                발생 원인
              </label>
              {readOnly ? (
                <div className="text-sm text-gray-900 py-2 whitespace-pre-wrap break-words">
                  {scarRootCause || "-"}
                </div>
              ) : (
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={scarRootCause}
                  onChange={(e) => setScarRootCause(e.target.value)}
                  placeholder="발생 원인을 입력하세요"
                />
              )}
            </div>
          </div>
        )}
      </section>

      {/* 담당자 선택 */}
      {!readOnly && (
        <section className={sectionStyle}>
          <div className="text-sm font-semibold mb-4 text-gray-900">
            담당자 선택
          </div>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {referenceUserDetails.length > 0 ? (
                <div className="flex flex-wrap gap-2 w-full">
                  {referenceUserDetails.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-800"
                    >
                      {u.name}
                      {u.position && ` (${u.position})`}
                      {u.company && ` - ${u.company}`}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  담당자가 선택되지 않았습니다.
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-basic text-sm px-3 py-1.5 w-full sm:w-auto"
              onClick={openReferenceModal}
            >
              담당자 선택
            </button>
          </div>
        </section>
      )}

      {/* 알림 설정 */}
      {!readOnly && (
        <section className={sectionStyle}>
          <div className="text-sm font-semibold mb-4 text-gray-900">
            알림 설정
          </div>
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-blue-600"
                checked={sendAlert}
                onChange={(e) => setSendAlert(e.target.checked)}
              />
              <span className="text-sm text-gray-700">
                담당자에게 알림 보내기
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 accent-red-600"
                checked={alertUrgent}
                disabled={!sendAlert}
                onChange={(e) => setAlertUrgent(e.target.checked)}
              />
              <span
                className={`text-sm ${
                  !sendAlert ? "text-gray-400" : "text-gray-700"
                }`}
              >
                긴급 알림
              </span>
            </label>
          </div>
        </section>
      )}

      {/* 첨부 파일 */}
      <section className={sectionStyle}>
        <div className="text-sm font-semibold mb-4 text-gray-900">
          첨부 파일
        </div>
        <MobileAttachmentBox
          ref={attachmentRef}
          dns={initial.dns}
          readOnly={readOnly}
        />
      </section>

      {/* 담당자 모달 */}
      <MobileReferenceModal
        isOpen={isReferenceModalOpen}
        onClose={closeReferenceModal}
        selectedIds={referenceUserIds}
        onChange={handleRefChange}
        onSave={handleRefSave}
        name="담당자"
        projectId={currentUser?.project_id}
      />
    </div>
  );
}
